import React, { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useDialogFocus } from '../../lib/useDialogFocus';

/**
 * Overlays of the context bar: popovers anchored under a chip on desktop, a
 * sheet on a phone. They escape the sticky topbar through a portal into
 * `.ops-command` (so its tokens apply), close on Escape and on a click
 * outside, trap focus while open and return it to what opened them.
 */

/** True below 720 px of viewport: the context bar becomes a summary and the panel a sheet. */
export function useNarrow(query = '(max-width: 719px)'): boolean {
  const get = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false);
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [query]);
  return narrow;
}

type Placement = { top: number; left: number; width: number; maxHeight: number };

/**
 * Where a popover goes under its anchor, in `.ops-command`'s own (zoomed)
 * coordinates: `position: fixed` children of the zoomed root are laid out in
 * pre-zoom px while getBoundingClientRect reports post-zoom px.
 */
export function useAnchoredPlacement(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  { width, align = 'start' }: { width: number; align?: 'start' | 'end' },
): Placement | null {
  const [placement, setPlacement] = useState<Placement | null>(null);
  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }
    const place = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const root = anchor.closest('.ops-command');
      const zoom =
        root instanceof HTMLElement ? Number.parseFloat(getComputedStyle(root).zoom || '1') || 1 : 1;
      const r = anchor.getBoundingClientRect();
      const origin = root instanceof HTMLElement ? root.getBoundingClientRect() : { left: 0, top: 0 };
      const pad = 12;
      const maxWidth = (window.innerWidth - pad * 2) / zoom;
      const w = Math.min(width, Math.max(240, maxWidth));
      let left = align === 'end' ? (r.right - origin.left) / zoom - w : (r.left - origin.left) / zoom;
      const minLeft = (pad - origin.left) / zoom;
      const maxLeft = (window.innerWidth - pad - origin.left) / zoom - w;
      left = Math.max(minLeft, Math.min(left, maxLeft));
      const top = (r.bottom + 6 - origin.top) / zoom;
      setPlacement({
        top,
        left,
        width: w,
        maxHeight: Math.max(200, (window.innerHeight - pad - (r.bottom + 6)) / zoom),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef, width, align]);
  return placement;
}

function portalRoot(anchor: HTMLElement | null): Element {
  return anchor?.closest('.ops-command') ?? document.body;
}

/**
 * A dialog anchored under a control (desktop) or a bottom sheet (phone).
 * `labelledBy` names it for assistive tech.
 */
export function ContextOverlay({
  open,
  anchorRef,
  onClose,
  width,
  align = 'start',
  sheet = false,
  className,
  labelledBy,
  label,
  children,
}: {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  width: number;
  align?: 'start' | 'end';
  /** Render as a bottom sheet (phones). */
  sheet?: boolean;
  className?: string;
  labelledBy?: string;
  label?: string;
  children: React.ReactNode;
}) {
  const placement = useAnchoredPlacement(open && !sheet, anchorRef, { width, align });
  const ref = useRef<HTMLDivElement | null>(null);
  useDialogFocus(ref, onClose, open && (sheet || !!placement));
  if (!open) return null;
  if (!sheet && !placement) return null;
  const style: React.CSSProperties | undefined = sheet
    ? undefined
    : { top: placement!.top, left: placement!.left, width: placement!.width, maxHeight: placement!.maxHeight };
  return createPortal(
    <>
      <div className={`cx-scrim${sheet ? ' is-sheet' : ''}`} onClick={onClose} role="presentation" />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : label}
        tabIndex={-1}
        className={`cx-overlay${sheet ? ' is-sheet' : ' is-popover'}${className ? ` ${className}` : ''}`}
        style={style}
      >
        {children}
      </div>
    </>,
    portalRoot(anchorRef.current),
  );
}

/**
 * Arrow keys inside a list: Up and Down (and Home / End) move focus between
 * the list's items (`[data-cx-item]`), so a long checklist is not a long
 * row of Tab stops to walk through.
 */
export function onListKeyDown(event: React.KeyboardEvent<HTMLElement>): void {
  const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
  if (!keys.includes(event.key)) return;
  const target = event.target as HTMLElement;
  // Radio inputs move between themselves natively; leave them be.
  if (target instanceof HTMLInputElement && target.type === 'radio') return;
  const items = [...event.currentTarget.querySelectorAll<HTMLElement>('[data-cx-item]')].filter(
    el => !(el as HTMLInputElement).disabled,
  );
  if (!items.length) return;
  const at = items.indexOf(target);
  let next = at;
  if (event.key === 'ArrowDown') next = at < 0 ? 0 : Math.min(items.length - 1, at + 1);
  if (event.key === 'ArrowUp') next = at < 0 ? 0 : Math.max(0, at - 1);
  if (event.key === 'Home') next = 0;
  if (event.key === 'End') next = items.length - 1;
  if (next === at) return;
  event.preventDefault();
  items[next]!.focus();
}
